<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <HeadlessUIDialog as="div" class="relative z-30">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full lg:ml-20 max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative"
            >
              <div class="absolute top-4 right-4">
                <XIcon class="w-6 h-6 cursor-pointer" @click="closePaymentModal" />
              </div>

              <div>
                <InsuranceSaleTitle :product="product" />
                <SuccessModal
                  v-if="paymentSuccess"
                  :customer="customer"
                  :matchedContractId="matchedContractId"
                  :isSepaPayment="isSepaPayment"
                />
                <div v-else>
                  <div v-show="showConfirmationStep">
                    <ConsentCode
                      :lead="lead"
                      :isVerifyingConsentCode="isVerifyingConsentCode"
                      :isSendingConsentCode="isSendingConsentCode"
                      @verifyConsentCode="verifyConsentCode"
                      @sendConsentCode="sendConsentCode"
                      @reportMissingCode="reportMissingCode"
                    />
                  </div>
                  <div v-show="!showConfirmationStep">
                    <OfferSelector
                      v-if="isPresent(selectedOfferSku)"
                      :offers="lead.offers"
                      :selectedOfferSku="selectedOfferSku"
                      :recommendedOfferSku="recommendedOfferSku"
                      :isMpcpCategory="isMpcpCategory"
                      @update:selectedOfferSku="handleOfferChange"
                    />
                    <StripePayment
                      ref="stripePayment"
                      v-if="selectedOfferPrice && selectedOfferPrice.stripeClient"
                      :offerPrice="selectedOfferPrice"
                      :discount="lead.discount"
                      :isSendingConsentCode="isSendingConsentCode"
                      @paymentSuccess="handlePaymentSuccess"
                      @showConfirmationStep="handleConfirmationStep"
                      @sendConsentCode="sendConsentCode"
                      @sendFailedPaymentLink="sendFailedPaymentLink"
                    />
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </HeadlessUIDialog>
  </TransitionRoot>
</template>

<script>
import { Dialog as HeadlessUIDialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { XIcon } from "@heroicons/vue/outline";

import InsuranceSaleTitle from "@/components/payment/InsuranceSaleTitle.vue";
import OfferSelector from "@/components/pos/subscription/payment/OfferSelector.vue";
import StripePayment from "@/components/pos/subscription/payment/StripePayment";
import SuccessModal from "@/components/pos/subscription/utils/SuccessModal.vue";
import ConsentCode from "@/components/pos/subscription/payment/ConsentCode.vue";

import LeadsService from "@/api/services/leads";

import { mapActions, mapGetters } from "vuex";
import { toCamelCase, isMpcpCategory as isMpcpCategoryUtil, isPresent } from "@estaly/ui";

export default {
  components: {
    HeadlessUIDialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    XIcon,
    InsuranceSaleTitle,
    OfferSelector,
    StripePayment,
    SuccessModal,
    ConsentCode,
  },

  props: {
    isOpen: Boolean,
    lead: Object,
    product: Object,
    customer: Object,
  },

  data: () => {
    return {
      selectedOfferSku: "",
      isOffersInitialized: false,
      paymentSuccess: false,
      matchedContractId: null,
      showConfirmationStep: false,
      isSepaPayment: false,
      isSendingConsentCode: false,
      isVerifyingConsentCode: false,
    };
  },

  watch: {
    "lead.offers": {
      immediate: true,
      deep: true,
      handler(newOffers) {
        if (isPresent(newOffers) && !this.isOffersInitialized) {
          this.isOffersInitialized = true;
          this.$nextTick(async () => {
            const sku = this.recommendedOfferSku;
            const recommendedExists = sku && newOffers.some((o) => o.contractSku === sku);
            this.selectedOfferSku = recommendedExists ? sku : newOffers[0].contractSku;
            if (this.selectedOfferSku !== newOffers[0].contractSku) {
              await this.handleOfferChange(this.selectedOfferSku);
            }
          });
        }
      },
    },
  },

  computed: {
    ...mapGetters("store", ["selectedStoreId"]),

    isMpcpCategory() {
      return isMpcpCategoryUtil(this.lead?.category);
    },

    recommendedOfferSku() {
      const id = this.lead?.advisoryAnswer?.recommendedOfferId;
      const title = this.lead?.advisoryAnswer?.recommendedOffer;
      const offers = this.lead?.offers;
      if (!(id || title) || !offers?.length) return null;
      const offer = offers.find((o) => (id != null && o.id === id) || (title && o.offerMarketingTitle === title));

      return offer?.contractSku ?? null;
    },

    selectedOfferPrice() {
      if (this.selectedOfferSku) {
        return this.lead.offers.find((offer) => offer.contractSku === this.selectedOfferSku);
      } else {
        return this.lead.offers[0];
      }
    },
  },

  methods: {
    isPresent,
    ...mapActions("notifications", ["notify"]),

    resetAttributes() {
      this.selectedOfferSku = "";
      this.isOffersInitialized = false;
      this.paymentSuccess = false;
      this.matchedContractId = null;
      this.showConfirmationStep = false;
      this.isSepaPayment = false;
      this.isSendingConsentCode = false;
      this.isVerifyingConsentCode = false;
    },

    closePaymentModal() {
      this.$emit("closePaymentModal", this.paymentSuccess);
      this.resetAttributes();
    },

    handlePaymentSuccess(paymentResult) {
      this.paymentSuccess = paymentResult.success;
      this.matchedContractId = paymentResult.matchedContractId;
      this.isSepaPayment = paymentResult.isSepaPayment || false;
    },

    handleConfirmationStep(value) {
      this.showConfirmationStep = value;
    },

    async handleOfferChange(newOfferSku) {
      if (!isPresent(newOfferSku)) {
        console.error("Invalid SKU provided");
        return;
      }

      const selectedOfferIndex = this.lead.offers.findIndex((offer) => offer.contractSku === newOfferSku);
      if (selectedOfferIndex === -1) {
        console.error("Selected offer not found");
        return;
      }

      const selectedOffer = this.lead.offers[selectedOfferIndex];

      const params = {
        uniqueToken: this.lead.estalyId,
        matchedContractId: selectedOffer.id,
      };

      const response = await LeadsService.createPaymentIntent(this.selectedStoreId, params);
      if (!response?.success) return;

      const data = toCamelCase(response.data);

      const updatedOffer = {
        ...selectedOffer,
        stripeClient: data.stripeClient,
        paymentIntentId: data.paymentIntentId,
      };

      this.$emit("updateOffer", { index: selectedOfferIndex, offer: updatedOffer });
      this.selectedOfferSku = updatedOffer.contractSku;
    },

    async sendConsentCode() {
      if (this.isSendingConsentCode) return;

      if (!isPresent(this.selectedOfferSku)) {
        await this.notify({
          category: "simple",
          type: "error",
          text: "Veuillez sélectionner une offre avant de continuer.",
        });
        return;
      }

      this.isSendingConsentCode = true;
      try {
        if (this.$refs.stripePayment && !(await this.$refs.stripePayment.validatePaymentDetails())) {
          return;
        }

        const response = await LeadsService.sendConsentCode(this.selectedStoreId, this.lead.id);
        if (!response?.success) return;

        this.showConfirmationStep = true;
        this.notify({
          category: "simple",
          type: "success",
          text: "The confirmation code has been sent.",
        });
      } finally {
        this.isSendingConsentCode = false;
      }
    },

    async verifyConsentCode(consentCode) {
      if (this.isVerifyingConsentCode) return;

      this.isVerifyingConsentCode = true;
      try {
        const response = await LeadsService.verifyConsentCode(this.selectedStoreId, this.lead.id, consentCode);
        if (response.status === 200) {
          await this.$refs.stripePayment.handleSubmit();
        } else if (response.status === 422) {
          this.notify({
            category: "simple",
            type: "error",
            text: "Le code est invalide. Veuillez réessayer.",
          });
        } else {
          this.notify({
            category: "simple",
            type: "error",
            text: "Une erreur est survenue lors de la vérification. Veuillez réessayer.",
          });
        }
      } catch (error) {
        console.error("Consent code verification failed:", error);
        this.notify({
          category: "simple",
          type: "error",
          text: "Une erreur est survenue lors de la vérification. Veuillez réessayer.",
        });
      } finally {
        this.isVerifyingConsentCode = false;
      }
    },

    async reportMissingCode() {
      const response = await LeadsService.reportMissingCode(this.selectedStoreId, this.lead.id);
      if (!response?.success) return;

      this.notify({
        category: "simple",
        type: "error",
        text: "We apologize for the inconvenience. We will contact the customer to finalize the sale.",
      });
      this.resetAttributes();
      this.$emit("closePaymentModal", true);
    },

    async sendFailedPaymentLink() {
      this.$emit("sendFailedPaymentLink");
    },
  },
};
</script>
